import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AttributeType, type AttributeEntityType } from "../../../clients/Purchaser/Attributes";
import PurchaserClient from "../../../clients/PurchaserClient";
import useDebounce from "../../Common/Hooks/useDebounce";
import { FloatingEmptyText, MenuCheckboxItem, Spinner, stopKeyPropagation } from "../../DesignSystem";
import type { AttributeFilter, StringAttributeFilter } from "../types";

type StringAttributeFilterSubmenuProps = {
    entityType: AttributeEntityType;
    attributeId: number;
    currentFilter?: StringAttributeFilter;
    setAttributeFilter: (filter: AttributeFilter) => void;
};

function StringAttributeFilterSubmenu({ entityType, attributeId, currentFilter, setAttributeFilter }: StringAttributeFilterSubmenuProps) {
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 300);

    const { data, isLoading } = useQuery({
        queryKey: ["attributeValues", entityType, attributeId, debouncedSearch],
        queryFn: () => PurchaserClient.getAttributeValues(entityType, attributeId, debouncedSearch || undefined),
    });

    const values = data?.values ?? [];
    const currentValues = currentFilter?.values ?? [];
    const currentSet = new Set(currentValues);

    const toggle = (value: string) => {
        const updated = currentSet.has(value) ? currentValues.filter(v => v !== value) : [...currentValues, value];
        setAttributeFilter({
            type: AttributeType.STRING,
            attributeId,
            values: updated,
        });
    };

    return (
        <>
            <input
                className="mb-1 w-full shrink-0 rounded border border-stone-200 px-2 py-1 text-sm shadow-sm outline-none placeholder:text-stone-400 focus:border-stone-400"
                placeholder="Search..."
                value={search}
                autoFocus
                onChange={e => setSearch(e.target.value)}
                onKeyDown={stopKeyPropagation}
            />
            {isLoading && (
                <div className="flex justify-center py-2">
                    <Spinner variant="inline" />
                </div>
            )}
            {!isLoading &&
                values.map(val => (
                    <MenuCheckboxItem key={val} checked={currentSet.has(val)} onChange={() => toggle(val)}>
                        {val}
                    </MenuCheckboxItem>
                ))}
            {!isLoading && values.length === 0 && <FloatingEmptyText>No values found</FloatingEmptyText>}
        </>
    );
}

export default StringAttributeFilterSubmenu;
